import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import { PlanningPageType } from './PlanningPage';
import RoleTag from './widgets/RoleTag';
import { useIncidentInfo, useUpdateIcsInfo } from '../hooks/incidentInfoHooks';
import { getIncidentCardTitle, imhMeetingGroundRules } from './USCGPage';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Progress,
  ProgressLabel,
  ProgressValue,
} from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';

type PlanningAgendaProps = {
  planningPhases: PlanningPageType[];
  currentPhase: number;
};

function progressPercent(current: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((current / total) * 100);
}

export const PlanningAgenda = ({
  planningPhases,
  currentPhase,
}: PlanningAgendaProps) => {
  const agenda = planningPhases[currentPhase].agenda;
  const agendaItemCount = agenda.length;

  const { data: incidentInfo } = useIncidentInfo();
  const mutate = useUpdateIcsInfo();

  const notes: string[] =
    incidentInfo?.agendaItems?.[currentPhase]?.notes ||
    Array(agendaItemCount).fill('');

  const checked: string[] =
    incidentInfo?.agendaItems?.[currentPhase]?.checked ||
    Array(agendaItemCount).fill('');

  const checkedCount = checked.filter(Boolean).length;

  const roleCounts: Record<string, number> = {};
  agenda.forEach((item) => {
    roleCounts[item.role] = (roleCounts[item.role] || 0) + 1;
  });

  const checkedRoleCounts: Record<string, number> = {};
  agenda.forEach((item, idx) => {
    if (checked[idx]) {
      checkedRoleCounts[item.role] = (checkedRoleCounts[item.role] || 0) + 1;
    }
  });

  const handleCheck = async (index: number, item: { role: string }) => {
    const updatedChecked = [...checked];
    if (updatedChecked[index] === '') {
      updatedChecked[index] = item.role;
    } else {
      updatedChecked[index] = '';
    }
    await mutate.mutateAsync({
      ...incidentInfo,
      agendaItems: {
        ...incidentInfo?.agendaItems,
        [currentPhase]: {
          ...(incidentInfo?.agendaItems &&
            incidentInfo?.agendaItems[currentPhase]),
          checked: updatedChecked,
        },
      },
    });
  };

  const handleSave = async (value: string, index: number) => {
    const updatedNotes = [...notes];
    updatedNotes[index] = value;
    await mutate.mutateAsync({
      ...incidentInfo,
      agendaItems: {
        ...incidentInfo?.agendaItems,
        [currentPhase]: {
          ...(incidentInfo?.agendaItems &&
            incidentInfo?.agendaItems[currentPhase]),
          notes: updatedNotes,
        },
      },
    });
  };

  return (
    <Card
      className="dark planning-widget tw:bg-background tw:text-foreground"
      size="sm"
    >
      <CardHeader className="tw:border-b">
        <CardTitle>{getIncidentCardTitle('agenda')}</CardTitle>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:rounded-lg tw:border tw:border-border tw:bg-muted/30 tw:p-3">
          <Progress
            value={progressPercent(checkedCount, agendaItemCount)}
            className="tw:w-full"
          >
            <ProgressLabel>Overall Progress</ProgressLabel>
            <ProgressValue>
              {() => `${checkedCount}/${agendaItemCount}`}
            </ProgressValue>
          </Progress>
          <div className="tw:mt-3 tw:grid tw:grid-cols-2 tw:gap-3">
            {Object.keys(roleCounts).map((role) => {
              const roleCurrent = checkedRoleCounts[role] || 0;
              const roleTotal = roleCounts[role];
              return (
                <Progress
                  key={role}
                  value={progressPercent(roleCurrent, roleTotal)}
                  className="tw:w-full"
                >
                  <ProgressLabel className="tw:min-w-0">
                    <RoleTag icsRoles={[role]} />
                  </ProgressLabel>
                  <ProgressValue>
                    {() => `${roleCurrent}/${roleTotal}`}
                  </ProgressValue>
                </Progress>
              );
            })}
          </div>
        </div>

        <div>
          <div className="tw:mb-1 tw:text-xs tw:font-semibold tw:text-muted-foreground">
            Ground Rules
          </div>
          <ul className="tw:mb-0 tw:list-disc tw:space-y-1 tw:ps-4 tw:text-sm">
            {imhMeetingGroundRules.map((rule) => (
              <li key={rule} className="tw:break-words">
                {rule}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="tw:mb-2 tw:text-xs tw:font-semibold tw:text-muted-foreground">
            Agenda
          </div>
          <div className="tw:flex tw:flex-col tw:gap-1">
            {agenda.map((item, index) => (
              <AgendaEntry
                key={`${currentPhase}-${index}-${item.item}`}
                text={item.item}
                role={item.role}
                checked={checked[index] !== ''}
                onCheck={() => handleCheck(index, item)}
                handleSave={(value) => handleSave(value, index)}
                note={notes[index]}
                index={index}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AgendaEntry = ({
  text,
  checked,
  onCheck,
  role,
  handleSave,
  note,
  index,
}: {
  text: string;
  checked: boolean;
  onCheck: () => void;
  role: string;
  handleSave: (value: string) => Promise<void>;
  note?: string;
  index: number;
}) => {
  const [showEdit, setShowEdit] = useState(false);
  const [draft, setDraft] = useState(note ?? '');
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    setDraft(note ?? '');
    setShowEdit(true);
  };

  const cancelEdit = () => {
    setDraft(note ?? '');
    setShowEdit(false);
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      await handleSave(draft);
      setShowEdit(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw:rounded-md tw:px-1 tw:py-1.5 tw:hover:bg-muted/40">
      <div className="tw:flex tw:items-start tw:gap-2">
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-start tw:gap-2">
          <Checkbox
            id={`${index}-agenda-item`}
            checked={checked}
            onCheckedChange={() => onCheck()}
            className="tw:mt-0.5"
            aria-label={text}
          />
          <div className="tw:min-w-0 tw:flex-1">
            <div className="tw:mb-0.5">
              <RoleTag icsRoles={[role]} />
            </div>
            <div className="tw:text-sm tw:leading-snug tw:select-none">
              {saving ? 'Saving…' : text}
            </div>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="tw:shrink-0"
          aria-label={showEdit ? 'Close note editor' : 'Edit agenda note'}
          onClick={() => (showEdit ? cancelEdit() : openEdit())}
        >
          <Pencil className="tw:size-3.5" aria-hidden />
        </Button>
      </div>
      {showEdit ? (
        <div className="tw:mt-2 tw:ms-6 tw:flex tw:flex-col tw:gap-2">
          <Textarea
            rows={2}
            placeholder="Add Notes..."
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={saving}
          />
          <div className="tw:flex tw:gap-2">
            <Button
              type="button"
              size="sm"
              disabled={saving}
              onClick={() => void saveNote()}
            >
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={saving}
              onClick={cancelEdit}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : note ? (
        <div className="tw:ms-6 tw:mt-1 tw:text-sm tw:text-muted-foreground tw:break-words">
          {note}
        </div>
      ) : null}
    </div>
  );
};
